import { CourseEnrollment } from "@/types";
import { defineStore, storeToRefs } from "pinia";
import { ref, computed, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { getCourses } from "@/api/courses";
import { useGlobalFilterStore } from "@/stores/globalFilterStore";

export type CourseTab = "all" | "in-progress" | "completed" | "draft";

export const useCoursesStore = defineStore("courses", () => {
    const globalFilterStore = useGlobalFilterStore();
    const { isSet: globalFilterIsSet, activeTaxonomySlugs } =
        storeToRefs(globalFilterStore);

    // The category slugs the active modality maps to. Courses are scoped to those
    // the modality is linked to via taxonomy, not the modality's own slug.
    const resolvedModality = computed(() => {
        if (!globalFilterIsSet.value) return null;
        return activeTaxonomySlugs.value;
    });

    const activeTab = ref<CourseTab>("all");

    const {
        isLoading,
        isFetching,
        data: courseEnrollments,
    } = useQuery({
        queryKey: ["course-enrollments"],
        queryFn: async () => {
            return await getCourses();
        },
    });

    // Reset tab when modality changes.
    watch(resolvedModality, () => {
        activeTab.value = "all";
    });

    const filteredCourses = computed(() => {
        let list = [...(courseEnrollments.value ?? [])];

        // Scope to active modality first, matching course categories against the
        // modality's mapped taxonomy slugs.
        if (resolvedModality.value && resolvedModality.value.length > 0) {
            const slugs = resolvedModality.value;
            list = list.filter((ce: CourseEnrollment) =>
                (ce.course.categories as any[]).some((c) =>
                    slugs.includes(c.slug)
                )
            );
        }

        // Apply tab filter.
        if (activeTab.value === "in-progress") {
            list = list.filter(
                (ce: CourseEnrollment) =>
                    ce.enrollment != null &&
                    ce.enrollment.status !== "new" &&
                    ce.enrollment.status !== "completed"
            );
        } else if (activeTab.value === "completed") {
            list = list.filter(
                (ce: CourseEnrollment) => ce.enrollment?.status === "completed"
            );
        } else if (activeTab.value === "draft") {
            list = list.filter(
                (ce: CourseEnrollment) => ce.course?.status === "draft"
            );
        }

        return list;
    });

    const isLoadingAll = computed(() => isLoading.value);

    return {
        isLoading,
        isFetching,
        courseEnrollments,
        isLoadingAll,
        activeTab,
        filteredCourses,
    };
});
